import type { WalletsSortBy, WalletsSortDir } from './wallets';
import { WALLETS_URL_PARAMS } from './wallets';

export const SETTLEMENTS_PATH = '/atlas';
export const SETTLEMENTS_LIST_PATH = '/atlas/list';
export const SETTLEMENTS_PARTICIPANTS_PATH = '/atlas/participants';
export const SETTLEMENTS_COUNTERPARTIES_PATH = '/atlas/counterparties';
export const EXTERNAL_EXCHANGE_PATH = '/atlas/exchanges';
export const COLLATERAL_MANAGEMENT_PATH = '/atlas/collateral';
export const COLLATERAL_MANAGEMENT_DASHBOARD_PATH = `${COLLATERAL_MANAGEMENT_PATH}/loans`;
export const AGREEMENTS_PATH = '/atlas/agreements';
export const OES_PATH = '/atlas/oes';

export const WEBHOOKS_PATH = '/webhooks';
export const API_V2_PATH = '/apis';
export const API_V2_CREATE_KEY_PATH = `${API_V2_PATH}/create-key`;
export const API_V2_VIEW_KEY_PATH = `${API_V2_PATH}/view-key/:key_id`;
export const API_V2_CREATE_ROLE_PATH = `${API_V2_PATH}/create-role`;
export const API_V2_EDIT_ROLE_PATH = `${API_V2_PATH}/edit-role/:role_id`;
export const API_V2_REFERENCE_IDS = `${API_V2_PATH}/reference-ids`;
export const API_V2_INTERNAL_TRANSFERS = `${API_V2_PATH}/shared-keys`;
export const API_V2_INTERNAL_TRANSFER_ENROLL = `${API_V2_INTERNAL_TRANSFERS}/enroll`;

export const DASHBOARD_PATH = `/dashboard`;
export const WALLETS_PATH = '/wallets';
export const DEFAULT_PATH = '/';
export const LOGIN_PATH = `/login`;
export const ACCESS_DENIED_PATH = '/access-denied';
const FRONTOFFICE_ACCESS_DENIED_PATH = `${ACCESS_DENIED_PATH}/`;
// Matched by apps/frontoffice/public/.well-known/apple-app-site-association
// (path + `sessionID` query key) — change together.
export const DASHBOARD_LOGIN_PATH = `/dashboard-login`;
// With `trailingSlash: true`, Next's client router rewrites the URL bar to
// this form on hydration whenever a query string is present (e.g. the
// `?sessionID=` link opened from the QR code) — the bare path is left alone.
// ALLOWED_ROUTES_FOR_MOBILE needs both forms since DeviceChecker matches
// window.location.pathname exactly.
const FRONTOFFICE_DASHBOARD_LOGIN_PATH = `${DASHBOARD_LOGIN_PATH}/`;
export const STATEMENTS_PATH = `/statements`;

// BALANCES
export const DASHBOARD_BALANCES = `${DASHBOARD_PATH}/balances`;

// EMAIL
export const EMAIL_VERIFICATION_PATH = '/verify-email';
export const FRONTOFFICE_EMAIL_VERIFICATION_PATH = `${EMAIL_VERIFICATION_PATH}/`;

// SIGNUP
const SIGNUP_PATH = `/signup`;
const FRONTOFFICE_SIGNUP_PATH = `${SIGNUP_PATH}/`;

// OPERATIONS
export const OPERATIONS_PATH = `/operations`;
export const OPERATIONS_DEPOSIT_ATTRIBUTIONS_PATH = `${OPERATIONS_PATH}/deposit-attributions`;

// SWAP
export const DAPP_SWAP_PATH = `/swap`;

// PORTFOLIO
export const PORTFOLIO_PATH = `${DASHBOARD_PATH}/portfolio`;

// VAULT
export const VAULT_PATH = '/vault';
const VAULT_TAB_PATH = `${VAULT_PATH}/:vault_id`;
export enum VAULT_TABS {
  portfolio = 'portfolio',
  wallets = 'wallets',
  // Top-level tab under FO_VAULT_NAV_V2 only
  nfts = 'nfts',
  settings = 'settings',
}

export const VAULT_DETAILS_INDIVIDUAL_NFT_PATH = `${VAULT_TAB_PATH}/nfts/:asset_type_id/:token_id`;
export const VAULT_DETAILS_COLLECTION_NFT_PATH = `${VAULT_TAB_PATH}/nfts/:asset_type_id`;
export const VAULT_PORTFOLIO_PATH = `${VAULT_TAB_PATH}/${VAULT_TABS.portfolio}`;
export const VAULT_WALLETS_PATH = `${VAULT_TAB_PATH}/${VAULT_TABS.wallets}`;
export const VAULT_SETTINGS_PATH = `${VAULT_TAB_PATH}/${VAULT_TABS.settings}`;
export const VAULT_PORTFOLIO_NFTS_PATH = `${VAULT_PORTFOLIO_PATH}/nfts`;
// Same /nfts prefix as the detail routes above, so list and detail sit together
export const VAULT_NFTS_PATH = `${VAULT_TAB_PATH}/${VAULT_TABS.nfts}`;

export const getVaultPaths = (
  vaultID: string,
  subPath: 'nfts' | 'portfolio' | 'settings' | 'wallets',
) => {
  const subPaths = {
    nfts: VAULT_TABS.nfts,
    portfolio: VAULT_TABS.portfolio,
    settings: VAULT_TABS.settings,
    wallets: VAULT_TABS.wallets,
  };
  return `${VAULT_PATH}/${vaultID}/${subPaths[subPath]}`;
};

export enum WALLET_TABS {
  BALANCES = 'balances',
  STAKING = 'staking',
  ACTIVITY = 'activity',
  SETTINGS = 'settings',
}

export const POOLED_ADDRESSES_HASH = 'pooled-addresses';

export const getVaultWalletPath = ({
  vaultID,
  walletID,
  tab = WALLET_TABS.BALANCES,
  hash,
}: {
  vaultID: string;
  walletID: string;
  tab?: WALLET_TABS;
  hash?: string;
}) => {
  const path = `${getVaultPaths(vaultID, 'wallets')}/${walletID}/${tab}`;
  return hash ? `${path}#${hash}` : path;
};

/**
 * Build URL to vault wallets tab with optional filters
 *
 * Supports filtering by multiple assets and networks using comma-separated values.
 *
 * @param params - Filter parameters for wallets tab
 * @returns URL path with query parameters
 *
 * @example
 * ```typescript
 * // Single asset filter
 * getVaultWalletsTabPath({
 *   vaultID: '123',
 *   assets: ['ETH_R']
 * });
 * // → /vault/123/wallets?assets=ETH_R
 *
 * // Multiple assets
 * getVaultWalletsTabPath({
 *   vaultID: '123',
 *   assets: ['SOL_TD', 'APT', 'AVAX']
 * });
 * // → /vault/123/wallets?assets=SOL_TD,APT,AVAX
 *
 * // Multiple networks
 * getVaultWalletsTabPath({
 *   vaultID: '123',
 *   networks: ['APTOS_MAINNET', 'AXELAR_MAINNET']
 * });
 * // → /vault/123/wallets?networks=APTOS_MAINNET,AXELAR_MAINNET
 * ```
 */
export const getVaultWalletsTabPath = ({
  vaultID,
  assets,
  networks,
  search,
  sortBy,
  sortDir,
}: {
  vaultID: string;
  assets?: string[];
  networks?: string[];
  search?: string;
  sortBy?: WalletsSortBy;
  sortDir?: WalletsSortDir;
}): string => {
  const basePath = getVaultPaths(vaultID, 'wallets');

  const queryParams: string[] = [];

  // Assets: comma-separated list (commas intentionally unencoded for nuqs compatibility)
  if (assets && assets.length > 0) {
    queryParams.push(`${WALLETS_URL_PARAMS.ASSETS}=${assets.join(',')}`);
  }

  // Networks: comma-separated list (commas intentionally unencoded for nuqs compatibility)
  if (networks && networks.length > 0) {
    queryParams.push(`${WALLETS_URL_PARAMS.NETWORKS}=${networks.join(',')}`);
  }

  // Search: URL-encoded string
  if (search) {
    queryParams.push(
      `${WALLETS_URL_PARAMS.SEARCH}=${encodeURIComponent(search)}`,
    );
  }

  // Sorting: both required together
  if (sortBy && sortDir) {
    queryParams.push(`${WALLETS_URL_PARAMS.SORT_BY}=${sortBy}`);
    queryParams.push(`${WALLETS_URL_PARAMS.SORT_DIR}=${sortDir}`);
  }

  return queryParams.length > 0
    ? `${basePath}?${queryParams.join('&')}`
    : basePath;
};

export const OPERATION_QUERY_STRING = 'op_id';

// SETTINGS
export const TRUSTED_DESTINATION_QUERY_STRING = 'destinationID';
const SETTINGS_PATH = '/settings';
export const DOCUMENT_DELIVERY_PATH = `${SETTINGS_PATH}/document-delivery`;
export const TRUSTED_SOURCES_PATH = `${SETTINGS_PATH}/trusted-sources`;
export const TRUSTED_DESTINATIONS_PATH = `${SETTINGS_PATH}/trusted-destinations`;
export const TRUSTED_SMART_CONTRACTS_PATH = `${SETTINGS_PATH}/trusted-smart-contracts`;
export const ADMINISTRATOR_POLICIES_PATH = `${SETTINGS_PATH}/policies`;
export const ACCESS_AND_POLICIES_PATH = `${SETTINGS_PATH}/access-and-policies`;
export const ACCESS_AND_POLICIES_POLICIES_PATH = `${ACCESS_AND_POLICIES_PATH}/policies`;
export const ACCESS_AND_POLICIES_ROLES_PATH = `${ACCESS_AND_POLICIES_PATH}/roles`;
export const ACCESS_AND_POLICIES_TEAM_PATH = `${ACCESS_AND_POLICIES_PATH}/team`;
export const INVOICES_DASHBOARD_PATH = `${SETTINGS_PATH}/invoices-dashboard`;
export const IP_ADDRESS_ALLOWLIST_PATH = `${SETTINGS_PATH}/ip-address-allowlist`;

export const getAccessAndPoliciesPolicyDetailPath = (policyID: string) => {
  return `${ACCESS_AND_POLICIES_POLICIES_PATH}/${policyID}`;
};
export const getAccessAndPoliciesPolicyDetailAdminsPath = (
  policyID: string,
) => {
  return `${getAccessAndPoliciesPolicyDetailPath(policyID)}/admins`;
};
export const getAccessAndPoliciesPolicyDetailUsersPath = (policyID: string) => {
  return `${getAccessAndPoliciesPolicyDetailPath(policyID)}/users`;
};
export const getAccessAndPoliciesPolicyDetailAssignedVaultsPath = (
  policyID: string,
) => {
  return `${getAccessAndPoliciesPolicyDetailPath(policyID)}/assigned-vaults`;
};

export const getAccessAndPoliciesRoleDetailPath = (roleId: string) => {
  return `${ACCESS_AND_POLICIES_ROLES_PATH}/${roleId}`;
};
export const getAccessAndPoliciesRoleDetailPermissionTabPath = (
  roleId: string,
) => {
  return `${ACCESS_AND_POLICIES_ROLES_PATH}/${roleId}/permissions`;
};
export const getAccessAndPoliciesRoleDetailRoleAssignmentsTabPath = (
  roleId: string,
) => {
  return `${ACCESS_AND_POLICIES_ROLES_PATH}/${roleId}/role-assignments`;
};

export const getAccessAndPoliciesTeamMemberDetailPath = (userId: string) => {
  return `${ACCESS_AND_POLICIES_TEAM_PATH}/${userId}`;
};

export const getAccessAndPoliciesTeamMemberDetailPoliciesPath = (
  userId: string,
) => {
  return `${getAccessAndPoliciesTeamMemberDetailPath(userId)}/policies`;
};

// SETTINGS WALLET KEYS - Porto only
export const WALLET_KEYS_PATH = `${SETTINGS_PATH}/wallet-keys`;
export const WALLET_KEYS_LIBRARY_PATH = `${WALLET_KEYS_PATH}/library`;
export const WALLET_KEYS_PREFERENCES_PATH = `${WALLET_KEYS_PATH}/preferences`;

// LEGAL DISCLOSURES
export const LEGAL_DISCLOSURE_ID_QUERY_STRING = 'disclosureId';

export const LEGAL_DISCLOSURES_PATH = `/legal-and-policies`;

export const getDisclosurePath = (disclosureId: string) => {
  return `${LEGAL_DISCLOSURES_PATH}/?${LEGAL_DISCLOSURE_ID_QUERY_STRING}=${disclosureId}`;
};

// IDENTITY & ACCESS MANAGEMENT
export const IAM_PATH = `${SETTINGS_PATH}/iam`;
export const IAM_ROLES_PATH = `${IAM_PATH}/roles`;
export const IAM_ROLE_PATH = `${IAM_PATH}/role`;
export const IAM_ROLE_DETAIL_PATH = `${IAM_ROLE_PATH}/:role_id`;
export const IAM_ROLE_DETAIL_ASSIGNMENTS_PATH = `${IAM_ROLE_DETAIL_PATH}/assignments`;
export const IAM_ROLES_CUSTOM_PATH = `${IAM_ROLES_PATH}/custom`;

// WEB 3 PORTAL
export const WEB3_PORTAL_PATH = `/web3-portal`;
export const WEB3_PORTAL_LIBRARY_PATH = `${WEB3_PORTAL_PATH}/library`;
export const WEB3_PORTAL_YOUR_APPS_PATH = `${WEB3_PORTAL_PATH}/your-apps`;
export const TRANSACTION_BUILDER_PATH = `/transaction-builder`;

export const TRANSFER_REQUEST_PURPOSE_QUERY = 'purpose';

// TRADE DASHBOARD
export const TRADE_PATH = `/trade`;
export const TRADE_HISTORY_PATH = `${TRADE_PATH}/history`;
export const TRADE_SETTLEMENT_PATH = `${TRADE_PATH}/settlement`;
export const TRADING_ASSETS_PATH = `/trading-assets`;

// TOKENIZATION
export const TOKENIZATION_PATH = `/tokenization`;

// STABLECOINS
export const STABLECOINS_PATH = `/stablecoins`;

// STABLECOINS TRANSACTIONS
export const STABLECOINS_TRANSACTIONS_PATH = `${STABLECOINS_PATH}/transactions`;

// STABLECOINS AUTOMATIONS
export const STABLECOINS_AUTOMATIONS_PATH = `${STABLECOINS_PATH}/automations`;

// STABLECOINS RESERVES
export const STABLECOINS_RESERVES_PATH = `${STABLECOINS_PATH}/reserves`;

// REPORTS
export const REPORTS_PATH = '/reports';
export const REPORTS_AVAILABLE = `${REPORTS_PATH}/available`;
export const REPORTS_LEGACY = `${REPORTS_PATH}/legacy`;

// TAX CENTER
export const TAX_CENTER_PATH = `/tax-center`;

// MOBILE
export const ALLOWED_ROUTES_FOR_MOBILE = [
  // An IP block hits mobile clients the same as desktop, and the denial is
  // the more useful message of the two.
  ACCESS_DENIED_PATH,
  FRONTOFFICE_ACCESS_DENIED_PATH,
  FRONTOFFICE_EMAIL_VERIFICATION_PATH,
  FRONTOFFICE_SIGNUP_PATH,
  DASHBOARD_LOGIN_PATH,
  FRONTOFFICE_DASHBOARD_LOGIN_PATH,
];

// REST API routes
export const VERIFY_WALLET_KEY_EXPORT_EMAIL_ROUTE =
  '/v1/verify_wallet_key_export_email?email_token=';

// External product URLs
export const PORTO_SIGNUP_URL = 'https://porto.signup.anchorage.com/';
export const ADX_PLATFORM_SELF_CUSTODY_URL =
  'https://www.anchorage.com/platform/self-custody';

// API Documentation
export const USER_DOCUMENTATION_BASE_URL = '/v1/docs';
export const DEVELOPER_DOCUMENTATION_BASE_URL = `/v1/devdocs`;
export const ADX_DOCS_URL_STAKING = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/staking`;
export const PORTO_WEB3_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/using-decentralized-apps`;
export const ADX_WEB3_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/web3`;
export const ADX_CHROME_EXTENSION_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/chrome-browser-extension`;
export const PORTO_CHROME_EXTENSION_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/browser-extension`;
export const ADX_ADD_APPS_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/adding-apps-to-your-approved-list`;
export const PORTO_SWAPPING_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/swap`;
export const ADX_STABLECOIN_REWARDS_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/stablecoin-rewards`;
export const THIRD_PARTY_PAYMENTS_INTERNATIONAL_WIRES_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/usdwithdrawals#international-wire-transfers-through-cross-river-bank-crb`;
export const ADB_QUORUM_LEARN_MORE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/quorums-and-sub-quorums`;
export const PORTO_QUORUM_LEARN_MORE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/creating-a-vault#set-vault-quorum-and-sub-quorums`;
export const ADB_BATCH_ENDORSEMENT_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/batched-endorsements`;
export const PORTO_BATCH_ENDORSEMENT_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/batch-endorsements`;
export const ADB_ADD_MUTIPLE_TRUSTED_DESTINATION_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/adding-trusted-destinations#adding-multiple-trusted-destinations-with-a-file-upload`;
export const PORTO_ADD_MUTIPLE_TRUSTED_DESTINATION_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/adding-multiple-trusted-destinations`;
export const PORTO_ADD_MUTIPLE_TRUSTED_SMART_CONTRACTS_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/adding-multiple-trusted-smart-contracts`;
export const FIND_TOKEN_CONTRACT_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/supported-assets#how-to-find-a-tokens-contract-address`;
export const BTC_STAKING_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/btc-staking-babylon`;
export const POLICIES_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/policies`;
export const GAS_STATION_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/gas-station`;
export const EARN_INTEREST_USER_GUIDE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/docs/interest-on-usd`;
// Knowledge Base (Mintlify) user docs. Legacy `/docs/<slug>` paths are only kept
// working by Mintlify's redirects, and some (e.g. /docs/stablecoins) land on the
// Knowledge Base home instead of the page. Prefer building new user-guide links
// from this base so they resolve to the migrated page directly (STA-1769).
export const KNOWLEDGE_BASE_USER_GUIDE_BASE_URL = `${USER_DOCUMENTATION_BASE_URL}?path=/knowledge-base/platform/users`;
export const STABLECOINS_USER_GUIDE_URL = `${KNOWLEDGE_BASE_USER_GUIDE_BASE_URL}/stablecoins`;
